import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import PosAPI from "../../api/inventory/posApi";
import StoreAPI from "../../api/StoreApi";
import Auth from "../../utils/AuthUtils";
import { showError } from "../../utils/toastUtils";
import { amountInWords, inr, round2 } from "../../utils/posBilling";
import { methodLabel } from "../../components/PaymentModal";
import "./pos.css";

const PAY_LABEL = { paid: "PAID", partial: "PARTIALLY PAID", unpaid: "UNPAID" };

function Invoice() {
  const { id } = useParams();
  const location = useLocation();
  const [sale, setSale] = useState(null);
  const [storeDetails, setStoreDetails] = useState(null);
  const [format, setFormat] = useState(localStorage.getItem("pos_print_format") || "a4");
 
  /********** get details of sale and store ***************/
  useEffect(() => {
    if (sessionStorage.getItem("storeDetails")) {
      setStoreDetails(JSON.parse(sessionStorage.getItem("storeDetails")));
    } else {
      fetchGeneralDetails();
    }
 
    fetchSale(id);
  }, [id]);
 
  const fetchSale = async (saleId) => {
    try {
      const res = await PosAPI.getSale(saleId);
      if (res.code === 200) {
        setSale(res.data.sale);
      } else showError(res.message);
    } catch (err) {
      showError("Invoice not found");
    }
  };
 
  const fetchGeneralDetails = async () => {
    try {
      const res = await StoreAPI.getGeneralDetails(Auth.getUserDetails("storeId"));
 
      if (res.code === 200) {
        const result = res.data;
        setStoreDetails(result);
        sessionStorage.setItem("storeDetails", JSON.stringify(result));
      }
    } catch (err) {}
  };
  /********** end get details of sale and store ***************/
 
  // POS se aaye to seedha print dialog
  useEffect(() => {
    if (sale && location.state?.autoPrint) {
      const t = setTimeout(() => window.print(), 400);
      window.history.replaceState({}, ""); // refresh par dobara print na ho
      return () => clearTimeout(t);
    }
  }, [sale, location.state]);
 
  const changeFormat = (f) => {
    setFormat(f);
    localStorage.setItem("pos_print_format", f);
  };
 
  // GST rate-wise summary (invoice ke neeche)
  const taxSlabs = useMemo(() => {
    if (!sale) return [];
    const map = {};
    sale.items.forEach((it) => {
      const k = it.gstRate;
      map[k] = map[k] || { rate: k, taxable: 0, tax: 0 };
      map[k].taxable += it.taxableAmount;
      map[k].tax += it.taxAmount;
    });
    return Object.values(map).map((s) => ({ ...s, taxable: round2(s.taxable), tax: round2(s.tax) }));
  }, [sale]);
 
  if (!sale) return <div className="content">Loading invoice...</div>;
 
  const t = sale.totals;
  const pay = sale.payment || {};
  const payStatus = pay.status || "unpaid";
  const cancelled = sale.status === "cancelled";
  const date = new Date(sale.createdAt);
  const storeAddress = [
    storeDetails?.address,
    storeDetails?.district,
    storeDetails?.state,
    storeDetails?.pincode,
  ]
    .filter(Boolean)
    .join(", ");
 
  return (
    <div className="content">
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3 no-print">
        <Link to="/pos/offline-sales" className="btn btn-outline-secondary">
          <i className="ti ti-arrow-left me-1" /> Offline Sales
        </Link>
        <div className="btn-group ms-auto">
          <button className={`btn btn-outline-primary ${format === "a4" ? "active" : ""}`} onClick={() => changeFormat("a4")}>
            A4
          </button>
          <button className={`btn btn-outline-primary ${format === "thermal" ? "active" : ""}`} onClick={() => changeFormat("thermal")}>
            Thermal 80mm
          </button>
        </div>
        <button className="btn btn-primary" onClick={() => window.print()}>
          <i className="ti ti-printer me-1" /> Print
        </button>
        <Link to="/pos" className="btn btn-success">
          <i className="ti ti-plus me-1" /> New Sale
        </Link>
      </div>
 
      <div className={`invoice-print invoice-${format}`}>
        {cancelled && <div className="invoice-watermark">CANCELLED</div>}
 
        {/* ---------- HEADER ---------- */}
        <div className="inv-head">
          <div>
            {storeDetails?.logo && <img src={storeDetails.logo} alt="logo" className="inv-logo" />}
            <h3 className="mb-1">{storeDetails?.name || "Your Store"}</h3>
            {storeAddress && <div>{storeAddress}</div>}
            {storeDetails?.phone && <div>Ph: {storeDetails.phone}</div>}
            {storeDetails?.email && <div>{storeDetails.email}</div>}
            {storeDetails?.gstin && <div><b>GSTIN:</b> {storeDetails.gstin}</div>}
          </div>
          <div className="text-end">
            <h4 className="mb-1">TAX INVOICE</h4>
            <div><b>Invoice No:</b> {sale.invoiceNo}</div>
            <div><b>Date:</b> {date.toLocaleDateString("en-IN")} {date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</div>
            <div><b>Sale Type:</b> Offline / Counter</div>
            <div className="mt-1">
              <span className={`pay-badge pay-${payStatus}`}>{PAY_LABEL[payStatus]}</span>
            </div>
          </div>
        </div>
 
        <div className="inv-bill-to">
          <b>Bill To:</b> {sale.customer?.name || "Walk-in Customer"}
          {sale.customer?.phone && <> · {sale.customer.phone}</>}
          {sale.customer?.email && <span className="col-a4"> · {sale.customer.email}</span>}
          {sale.customer?.gstin && <> · GSTIN: {sale.customer.gstin}</>}
        </div>
 
        {/* ---------- ITEMS ---------- */}
        <table className="inv-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Item</th>
              <th className="col-a4">HSN</th>
              <th className="text-end">Qty</th>
              <th className="text-end">Rate</th>
              <th className="text-end col-a4">GST%</th>
              <th className="text-end col-a4">Taxable</th>
              <th className="text-end">Amount</th>
            </tr>
          </thead>
          <tbody>
            {sale.items.map((it, i) => (
              <tr key={`${it.variantId}-${i}`}>
                <td>{i + 1}</td>
                <td>
                  {it.title}
                  {it.variantName && <small className="d-block">{it.variantName}</small>}
                  <small className="d-block text-muted col-a4">{it.sku}</small>
                </td>
                <td className="col-a4">{it.hsnCode}</td>
                <td className="text-end">{it.qty} {it.unit}</td>
                <td className="text-end">
                  {it.priceEdited && it.listPrice > it.unitPrice && (
                    <small className="d-block text-muted text-decoration-line-through col-a4">{inr(it.listPrice)}</small>
                  )}
                  {inr(it.unitPrice)}
                </td>
                <td className="text-end col-a4">{it.gstRate}%</td>
                <td className="text-end col-a4">{inr(it.taxableAmount)}</td>
                <td className="text-end">{inr(it.netAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
 
        {/* ---------- TOTALS ---------- */}
        <div className="inv-bottom">
          <div className="inv-slabs col-a4">
            <table className="inv-table small">
              <thead>
                <tr>
                  <th>GST %</th>
                  <th className="text-end">Taxable</th>
                  <th className="text-end">CGST</th>
                  <th className="text-end">SGST</th>
                </tr>
              </thead>
              <tbody>
                {taxSlabs.map((s) => (
                  <tr key={s.rate}>
                    <td>{s.rate}%</td>
                    <td className="text-end">{inr(s.taxable)}</td>
                    <td className="text-end">{inr(s.tax / 2)}</td>
                    <td className="text-end">{inr(s.tax / 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2"><b>Amount in words:</b> {amountInWords(t.grandTotal)}</div>
          </div>
 
          <div className="inv-totals">
            <div><span>Sub Total</span><span>{inr(t.subtotal)}</span></div>
            {t.couponDiscount > 0 && (
              <div>
                <span>Coupon{sale.coupon?.code ? ` (${sale.coupon.code})` : ""}</span>
                <span>− {inr(t.couponDiscount)}</span>
              </div>
            )}
            {(t.manualDiscount ?? (t.couponDiscount ? 0 : t.discount)) > 0 && (
              <div>
                <span>Discount{sale.discountInput?.mode === "percent" ? ` (${sale.discountInput.value}%)` : ""}</span>
                <span>− {inr(t.manualDiscount ?? t.discount)}</span>
              </div>
            )}
            <div><span>Taxable Value</span><span>{inr(t.taxable)}</span></div>
            <div><span>CGST</span><span>{inr(t.cgst)}</span></div>
            <div><span>SGST</span><span>{inr(t.sgst)}</span></div>
            {t.roundOff !== 0 && <div><span>Round Off</span><span>{inr(t.roundOff)}</span></div>}
            <div className="grand"><span>Grand Total</span><span>{inr(t.grandTotal)}</span></div>
            <small className="d-block text-end">(Inclusive of all taxes)</small>
            {(t.itemDiscount || 0) + (t.discount || 0) > 0 && (
              <div className="inv-saved">You saved {inr((t.itemDiscount || 0) + (t.discount || 0))} on this bill</div>
            )}
 
            {/* ---------- PAYMENT ---------- */}
            <div className="inv-payment">
              <div className="inv-payment-title">
                <span>Payment</span>
                <span className={`pay-badge pay-${payStatus}`}>{PAY_LABEL[payStatus]}</span>
              </div>
              {(pay.entries || []).map((e, i) => (
                <div key={e._id || i}>
                  <span>
                    {methodLabel(e.method)}
                    {e.reference && <small className="text-muted"> ({e.reference})</small>}
                    {i > 0 && new Date(e.receivedAt).toDateString() !== date.toDateString() && (
                      <small className="text-muted d-block">on {new Date(e.receivedAt).toLocaleDateString("en-IN")}</small>
                    )}
                  </span>
                  <span>{inr(e.amount)}</span>
                </div>
              ))}
              {pay.changeReturned > 0 && (
                <>
                  <div className="text-muted"><span>Cash received</span><span>{inr(pay.cashTendered)}</span></div>
                  <div className="text-muted"><span>Change returned</span><span>{inr(pay.changeReturned)}</span></div>
                </>
              )}
              <div className="fw-semibold"><span>Amount Paid</span><span>{inr(pay.paidAmount)}</span></div>
              {pay.dueAmount > 0 && (
                <div className="fw-bold text-danger"><span>Balance Due</span><span>{inr(pay.dueAmount)}</span></div>
              )}
            </div>
          </div>
        </div>
 
        {sale.note && <div className="mt-2"><b>Note:</b> {sale.note}</div>}
        {cancelled && (
          <div className="mt-2 text-danger">
            <b>Cancelled</b> on {new Date(sale.cancelledAt).toLocaleString("en-IN")}
            {sale.cancelReason && <> — {sale.cancelReason}</>}
          </div>
        )}
 
        <div className="inv-footer">
          <div>Thank you for shopping with us!</div>
          <div className="col-a4 text-end">
            <div style={{ height: 40 }} />
            Authorised Signatory
          </div>
        </div>
      </div>
    </div>
  );
}

export default Invoice;